import { useMemo } from "react";
import useGetMySelf from "@/features/auth/use-get-my-self";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import { PathPickMarketSyncKind } from "@/features/pathpick/types";
import { PATH_PICK_MARKET_SYNC_KINDS } from "@/features/pathpick/utils";

// 네이버 외 마켓 연동 권한 — 바이어(회사) features 의 채널별 값. 어드민이 바이어별로 켠다.
// 네이버는 패스픽 멤버면 기본 허용이라 값이 없다. (서버 판정: services/pathpick_market_permission.py)
// 예전 묶음 권한 PATHPICK_SYNC_ALL 은 더 이상 보지 않는다 — 서버도 채널별 값으로만 판정한다.
export const PATHPICK_SYNC_FEATURE_BY_KIND: Record<
  Exclude<PathPickMarketSyncKind, "NAVER_SYNC">,
  string
> = {
  TRENBE_SYNC: "PATHPICK_SYNC_TRENBE",
  MUSTIT_SYNC: "PATHPICK_SYNC_MUSTIT",
  CAFE24_SYNC: "PATHPICK_SYNC_CAFE24",
};

// features → API 연동으로 신청할 수 있는 마켓(표시 순서는 PATH_PICK_MARKET_SYNC_KINDS). 네이버는 항상 포함.
export const getPermittedPathPickSyncKinds = (
  features: string[] | null | undefined
): PathPickMarketSyncKind[] =>
  PATH_PICK_MARKET_SYNC_KINDS.filter(
    (kind) =>
      kind === "NAVER_SYNC" ||
      Boolean(features?.includes(PATHPICK_SYNC_FEATURE_BY_KIND[kind]))
  );

/**
 * 현재 회사가 API 연동으로 신청할 수 있는 마켓 목록 — ["NAVER_SYNC"]뿐이면 네이버 연동만.
 * 둘 이상이면 연동 확인 모달에서 마켓을 고르고, 신청함·검색의 "신청 이력" 조건에도 그 마켓들이 들어간다.
 */
const usePathPickSyncKinds = (): PathPickMarketSyncKind[] => {
  const { data } = useGetMySelf();
  const { companyId } = useCompanyIdentity();

  const features = data?.companies.find(
    (company) => company.id === companyId
  )?.features;
  // 목록을 의존성으로 쓰는 곳(useMemo 등)이 렌더마다 다시 계산되지 않게 같은 features 면 같은 배열을 준다.
  const featuresKey = (features ?? []).join(",");
  return useMemo(
    () => getPermittedPathPickSyncKinds(features),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [featuresKey]
  );
};

export default usePathPickSyncKinds;
